import { useRouter } from 'next/router';

const PLATFORM_QUERY_KEY = 'platform';

const ALLOWED_PLATFORMS = ['native-app', 'android-app'] as const;

export type EmbeddedPlatform = (typeof ALLOWED_PLATFORMS)[number];

const isValidPlatform = (value: unknown): value is EmbeddedPlatform =>
  typeof value === 'string' && (ALLOWED_PLATFORMS as readonly string[]).includes(value);

/**
 * Returns the current embedded platform ('native-app' | 'android-app' | null).
 *
 * Detection: reads the `?platform=` query parameter directly. No persistence —
 * the native webview controls all URLs it opens and is responsible for
 * appending the parameter on each navigation.
 *
 * When the parameter is absent the hook returns null, which restores the
 * full browser layout (header, sidebar, footer).
 */
const usePlatform = (): EmbeddedPlatform | null => {
  const { query } = useRouter();
  const value = query[PLATFORM_QUERY_KEY];
  return isValidPlatform(value) ? value : null;
};

export default usePlatform;
